import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import {
    UPSERT_EVENTS,
    UPSERT_WH_EVENT,
    UPDATE_ORDERS_MANY,
    QUERY_ORDERS_WITH_DRIVERS_BY_CARRIER_ID_OR_SHIPPER_ID,
    SEND_MESSAGES,
    GET_CARRIERS,
    QUERY_ORDER_SHIPPERS_FOR_CARRIER,
} from './queries';
import {
    createTheme,
    Box,
    Grid,
    ThemeProvider,
    TextField,
    InputAdornment,
    LinearProgress,
    Typography,
    MenuItem,
    Link,
} from '@material-ui/core';
import { useSearchParams } from 'react-router-dom';
import { Search } from '@material-ui/icons';
import MyOrdersColumnFilterPanel, { COLUMN_FILTER_MAP as MYORDERS_COLUMN_FILTER_MAP } from './ColumnFilterPanel';
import CustomizableTable from '@/components/CustomizableTable';
import { useColumns } from './columns';
import { useRowHighlight } from '../admin/AdminOrders/constants';
import { OnwardTabContainer, OnwardTab } from '../Tabs';
import { UserContext } from '../App';
import { useClientUser, useOrderTableProfiles } from '@/hooks';
import './styles.css';
import _, { zipObject } from 'lodash';
import add from 'date-fns/add';
import formatISO from 'date-fns/formatISO';
import { captureException } from '@sentry/react';
import { css } from '@emotion/react';
import { PrimaryHeaderContainer, PrimaryHeaderLabel } from './blocks';
import { FILTER_ATTRS } from './constants';
import {
    EXPORT_ONLY_COLUMNS,
    buildExportColumnList,
    flattenTableLeafColumns,
    partitionSplitParents,
    bulkSkipMessage,
} from './utils';

import { PrimaryButton } from '@/styles/blocks';
import { aggregateEvents, calcMilestone, patchEvent, receive } from '@onward-delivery/core';
import { RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import { startCase } from 'lodash';
import Snackbar from '../Snackbar';
import { useTabs } from '../admin/AdminOrders/constants';
import TabScrollButton from '@material-ui/core/TabScrollButton';
import { calcOrderBreakdown, calcOrderPricing, calcOrderOwner } from '@/utilities/calcOrderPricing';
import useAction from '@/utilities/useQuery';
import {
    findMatchingWarehouseEvent,
    toWarehouseEventInput,
    withWarehouseEstimateEvent,
} from '@/utilities/warehouseEvents';
import { buildBulkReceiveTrackingEvents, buildBulkCompletionTrackingEvents } from './trackingEventHelpers';
import SendMessageModal from './SendMessageModal';
import ExportCsvTemplateModal from '../ExportCsvTemplateModal';
import {
    ITEM_LINKED_EXPORT_HEADERS,
    OMS_ACCESSORIAL_EXPORT_COLUMNS,
    ORDER_EXPORT_COLUMNS,
} from '../ExportCsvTemplateModal/constants';
import { INSERT_CSV_EXPORT_TEMPLATE } from '@/graphql/mutations/csv_export_templates';
import { useExportTemplateOwnerId } from '../ExportCsvTemplateModal/useExportTemplateOwnerId';
import csvDownload from 'json-to-csv-export';
import { genAccessorials } from '../Account/Tariffs/utils';
import { MILESTONE_OPTIONS } from '@onward-delivery/core';
import { useOrderColor } from '@/components/Account/MilestoneThresholds/util';
import { useNavigate } from 'react-router-dom';
import CustomizableTableProfileControl from '@/components/CustomizableTable/CustomizableTableProfileControl';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import { UPSERT_ORDERS } from '@/components/ShipmentForm/graphql/mutations';
import { QUERY_ONWARD_ADMINS } from '@/graphql/queries/users';
import { ASSET_HEAVY } from '@/constants/featureFlags';
import { computeOrgClientIds } from '@/utilities/orgClientIds';
import { carrierDisplayName } from '@/utilities/carrierAlias';

const EXPORT_DATE_SHORT_FMT = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});
const EXPORT_DATE_TIME_FMT = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
});
const EXPORT_INT_FMT = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const formatExportDate = (val, formatter = EXPORT_DATE_SHORT_FMT) => {
    if (val == null || val === '') return '-';
    const d = val instanceof Date ? val : new Date(val);
    return isNaN(d) || !d.getTime() ? '-' : formatter.format(d);
};

const exportHasAnyValue = (val) => {
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'string') return val.trim().length > 0;
    if (val && typeof val === 'object') return Object.keys(val).length > 0;
    return !!val;
};

const normalizeRangeForExport = (metric) => {
    if (!metric) return 0;
    return Array.isArray(metric) ? Math.max(...metric.map((w) => parseFloat(w))) : parseFloat(metric);
};

const sumOrderMetric = (order, totalField, perItemFields) => {
    let total =
        normalizeRangeForExport(order?.[totalField.aggregate]) + normalizeRangeForExport(order?.[totalField.alt]);
    if (!total) {
        total = 0;
        ['items', 'returns'].forEach((field) => {
            (order?.[field] || []).forEach((item) => {
                total +=
                    normalizeRangeForExport(item[perItemFields.primary]) ||
                    normalizeRangeForExport(item[perItemFields.fallback]);
            });
        });
    }
    return total;
};

const orderTotalWeightForExport = (order) =>
    sumOrderMetric(
        order,
        { aggregate: 'items_total_weight', alt: 'totalWeight' },
        { primary: 'total_weight', fallback: 'weight' }
    );

const orderTotalCubesForExport = (order) =>
    sumOrderMetric(
        order,
        { aggregate: 'items_total_cubes', alt: 'totalCubes' },
        { primary: 'total_cubes', fallback: 'cubes_per_unit' }
    );

const ITEM_EXPORT_HEADERS = new Set(['Item', ...ITEM_LINKED_EXPORT_HEADERS]);
const NON_EXPORTABLE_COLUMN_IDS = new Set(['show_hidden_columns']);

const theme = createTheme({
    typography: {
        fontFamily: ['Montserrat', 'Roboto', 'Arial'].join(','),
        h2: {
            fontWeight: 800,
            fontSize: '16px',
            lineHeight: 1.25,
            color: '#2B2B2B',
        },
        h3: {
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: 1.25,
            color: '#2B2B2B',
        },
    },
    palette: {
        text: {
            secondary: '#4c4c4c',
        },
        primary: {
            main: '#59b863',
            contrastText: '#fff',
        },
    },
});

const searchable = [
    'order_number',
    'po_number',
    'pickup_city',
    'pickup_state',
    'pickup_zip',
    'dropoff_name',
    'dropoff_city',
    'dropoff_state',
    'dropoff_zip',
    'reference_id',
    'dropoff_phone',
    'manufacturer',
    'middle_mile_integration_meta.tracking_number',
    'dispatch_integration_meta.tracking_number',
];

const addOneDay = (date) => {
    const alteredDate = add(new Date(date), { days: 2 });
    return formatISO(alteredDate, { representation: 'date' });
};

const DEFAULT_SORT_BY = { id: 'created_at', desc: true };
const DEFAULT_TABLE_INITIAL_STATE = {
    hiddenColumns: [
        // Wide, and most users want city/state/zip. Both stay exportable while hidden.
        'destination_address',
        'destination_address_2',
        'loaded_at',
        'ofd_at',
        'arrived_at',
        'completed_at',
        'pod_present',
        'photos_present',
        'assigned_admin',
    ],
    columnOrder: [],
    columnResizing: { columnWidths: {} },
};

const getMyOrdersProfileFilterKeys = () => {
    const keys = new Set();
    Object.values(MYORDERS_COLUMN_FILTER_MAP || {}).forEach((cfg) => {
        (cfg?.controls || []).forEach((control) => {
            if (control?.key) keys.add(control.key);
            if (Array.isArray(control?.keys)) control.keys.forEach((k) => keys.add(k));
            if (control?.type === 'serviceType') {
                keys.add('serviceType');
                keys.add('locationType');
            }
        });
    });
    return Array.from(keys);
};

const MYORDERS_PROFILE_FILTER_KEYS = getMyOrdersProfileFilterKeys();

const MyOrders = () => {
    const navigate = useNavigate();
    let [searchParams, setSearchParams] = useSearchParams();
    const [searchOverride, setSearch] = useState('');
    const [tabIndex, setTabIndex] = useState(0);
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
    const [totalCount, setTotalCount] = useState(0);
    const [openSendMessage, setOpenSendMessage] = useState(false);
    const [openExportModal, setOpenExportModal] = useState();
    const [editingOrder, setEditingOrder] = useState(null);
    const apolloClient = useApolloClient();

    const [notification, setNotification] = useState({});

    const [selectedOrderIds, setSelectedOrderIds] = useState({});
    const [selectAllActive, setSelectAllActive] = useState(false);
    const [excludedOrderIds, setExcludedOrderIds] = useState({});
    const [openColumnFilter, setOpenColumnFilter] = useState(null);

    const clearSelection = useCallback(() => {
        setSelectAllActive(false);
        setExcludedOrderIds({});
        setSelectedOrderIds({});
    }, []);

    const { user, sidebarCollapsed, admin_clients } = useContext(UserContext);
    const {
        accountType,
        milestone_threshold_settings,
        userType,
        user_id,
        teammateRoles,
        isOnwardAdmin,
        circles,
        ...client
    } = useClientUser();
    const templateOwnerId = useExportTemplateOwnerId();
    const isAdmin = user?.roles?.['ADMIN'] || user?.roles?.['ONWARD_ADMIN'] || isOnwardAdmin;
    const isCorporateAdminCombinedView = !!user?.roles?.['ADMIN'] && !client?.client_id;

    const { shipperIds: orgShipperIds, carrierIds: orgCarrierIds } = useMemo(() => {
        if (!isCorporateAdminCombinedView) return { shipperIds: null, carrierIds: null };
        return computeOrgClientIds(admin_clients);
    }, [isCorporateAdminCombinedView, admin_clients]);

    const isOrderEditable = useCallback(
        (order) => {
            if (order?.read_only) return false;
            const adminEditable = isOnwardAdmin || isCorporateAdminCombinedView;
            const teammateEditable = userType !== 'teammate' || teammateRoles?.operations;
            const carrierEditable = user_id === order?.carrier_id || user_id === order?.contractor_id;
            const shipperLocked =
                order?.carrier_id &&
                ['inProgress', 'routedInProgress', 'complete', 'routedComplete'].includes(order?.order_status);
            const shipperEditable = user_id === order?.shipper_id && !shipperLocked;
            return adminEditable || (teammateEditable && (carrierEditable || shipperEditable));
        },
        [isOnwardAdmin, isCorporateAdminCombinedView, teammateRoles?.operations, userType, user_id]
    );

    // Profiles (CustomizableTable settings templates)
    const [tableInitialState, setTableInitialState] = useState(() => DEFAULT_TABLE_INITIAL_STATE);
    const [tableKey, setTableKey] = useState(0);
    const [tableLayoutState, setTableLayoutState] = useState({
        hiddenColumns: [],
        columnOrder: [],
        columnWidths: {},
    });

    const applyFilters = useCallback(
        (values = {}) => {
            setSearchParams((prev) => {
                Object.entries(values).forEach(([attr, val]) => {
                    if (val !== null && val !== undefined && val !== '') {
                        prev.set(attr, val);
                    } else {
                        prev.delete(attr);
                    }
                });

                return prev;
            });
        },
        [setSearchParams]
    );

    const buildProfileSettings = useCallback(() => {
        const filterValues = {};
        (MYORDERS_PROFILE_FILTER_KEYS || []).forEach((key) => {
            const val = searchParams.get(key);
            if (val !== null && val !== undefined && val !== '') {
                filterValues[key] = val;
            }
        });

        return {
            layout: {
                hiddenColumns: tableLayoutState.hiddenColumns || [],
                columnOrder: tableLayoutState.columnOrder || [],
                columnWidths: tableLayoutState.columnWidths || {},
            },
            sorting: {
                sortBy: sortBy?.id ? [{ id: sortBy.id, desc: !!sortBy.desc }] : [],
            },
            filters: {
                values: filterValues,
            },
        };
    }, [searchParams, sortBy, tableLayoutState]);

    const applyProfileSettings = useCallback(
        (settings) => {
            const layout = settings?.layout || {};
            setTableInitialState({
                hiddenColumns: layout.hiddenColumns || DEFAULT_TABLE_INITIAL_STATE.hiddenColumns,
                columnOrder: layout.columnOrder || DEFAULT_TABLE_INITIAL_STATE.columnOrder,
                columnResizing: { columnWidths: layout.columnWidths || {} },
            });
            setTableKey((k) => k + 1);

            const nextSort = settings?.sorting?.sortBy?.[0];
            if (nextSort?.id) {
                setSortBy({ id: nextSort.id, desc: !!nextSort.desc });
            } else {
                setSortBy(DEFAULT_SORT_BY);
            }

            const resetValues = Object.fromEntries((MYORDERS_PROFILE_FILTER_KEYS || []).map((k) => [k, null]));
            const savedValues = settings?.filters?.values || {};
            applyFilters({ ...resetValues, ...savedValues });
        },
        [applyFilters]
    );

    const resetMyOrdersToDefaults = useCallback(() => {
        setTableInitialState(DEFAULT_TABLE_INITIAL_STATE);
        setTableKey((k) => k + 1);
        setSortBy(DEFAULT_SORT_BY);

        const resetValues = Object.fromEntries((MYORDERS_PROFILE_FILTER_KEYS || []).map((k) => [k, null]));
        applyFilters(resetValues);
    }, [applyFilters]);

    const {
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,
        profileSelectProps,
        resetToDefaultProfile,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleDeleteCurrentProfile,
        handleImportProfileById,
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,
    } = useOrderTableProfiles({
        tableType: 'MY_ORDERS',
        userId: user?.user_id,
        buildProfileSettings,
        applyProfileSettings,
        onResetToDefault: resetMyOrdersToDefaults,
        notifyError: (message) => setNotification({ severity: 'error', message }),
    });

    const [clientIds, clientsById] = useMemo(() => {
        const clients = !!client?.client_id ? [client] : admin_clients || [];
        return [
            clients.map((client) => client.client_id),
            Object.fromEntries(clients.map((client) => [client.client_id, client])),
        ];
    }, [admin_clients, client]);

    const shipperOptionsFromClients = useMemo(() => {
        const shippersById = Object.values(clientsById).reduce((acc, c) => {
            const roles = c?.roles || c?.user?.roles || {};
            const isShipper = !!roles?.['SHIPPER'];
            const isCarrier = !!(roles?.['CARRIER'] || roles?.['CONTRACTOR']);

            const clientId = c?.client_id;
            const clientName = c?.businessName || c?.business_name;

            const clientAsShipper = isShipper && clientId && clientName ? { [clientId]: clientName } : {};

            const partnerships = c?.shipping_partners || c?.carrier_partnerships || [];
            const shippingPartners = isCarrier
                ? Object.fromEntries(
                      partnerships
                          .filter((p) => p?.shipper_id)
                          .map((p) => [
                              p.shipper_id,
                              p.shipper_alias || p.shipper_label || p?.shipper?.business_name || p.shipper_id,
                          ])
                  )
                : {};

            return { ...acc, ...clientAsShipper, ...shippingPartners };
        }, {});

        return Object.entries(shippersById)
            .map(([shipper_id, shipper_name]) => ({
                value: shipper_id,
                label: shipper_name,
            }))
            .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
    }, [clientsById]);

    const nonCorpAdminOrdersTable = !isCorporateAdminCombinedView && accountType === 'carrier' && !!user_id;
    const { data: shipperData } = useQuery(QUERY_ORDER_SHIPPERS_FOR_CARRIER, {
        variables: { user_id },
        skip: !nonCorpAdminOrdersTable,
    });
    const shipperOptionsFromOrders = useMemo(() => {
        const byId = new Map();
        (shipperData?.orders || []).forEach((o) => {
            const id = o?.shipper_id;
            const name = o?.order_shipper?.business_name;
            if (id && name) byId.set(id, name);
        });
        return Array.from(byId.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
    }, [shipperData]);

    const shipperOptions = useMemo(() => {
        return nonCorpAdminOrdersTable ? shipperOptionsFromOrders : shipperOptionsFromClients;
    }, [nonCorpAdminOrdersTable, shipperOptionsFromOrders, shipperOptionsFromClients]);

    const filterableColumnIds = useMemo(
        () => [
            'created_at',
            'deldate',
            'est_ship_date',
            'est_received_date',
            'received_date',
            'warehousestatus',
            'manufacturer',
            'order_type',
            'service_type',
            'shipper',
            'carrier',
            'assigned_admin',
            'dispatch_integration_type',
            'middle_mile_integration_status',
            'vol',
            'milestone',
        ],
        []
    );

    const { data: onwardAdminsData } = useQuery(QUERY_ONWARD_ADMINS);
    const onwardAdmins = useMemo(() => onwardAdminsData?.users || [], [onwardAdminsData]);
    const onwardAdminsById = useMemo(
        () =>
            Object.fromEntries(
                onwardAdmins.map((u) => [u.user_id, u.username || u.email || u.user_id]).filter(([id]) => !!id)
            ),
        [onwardAdmins]
    );
    const onwardAdminOptions = useMemo(
        () => [
            { value: '', label: 'All' },
            ...onwardAdmins.map((u) => ({
                value: u.user_id,
                label: u.username || u.email || u.user_id,
            })),
        ],
        [onwardAdmins]
    );

    const { data: carriersData } = useQuery(GET_CARRIERS, {
        variables: {
            where: {
                _or: [
                    { shipper_id: { _in: clientIds } },
                    { carrier_id: { _in: clientIds } },
                    { middle_mile_carrier_id: { _in: clientIds } },
                    { contractor_id: { _in: clientIds } },
                ],
            },
        },
    });
    // Aliased alongside the Agent column — filtering by a name the shipper is never shown
    // would make the page incoherent. The value stays the real carrier_id.
    const carrierOptions = useMemo(() => {
        const distinctCarrierOrders = carriersData?.results || [];
        return distinctCarrierOrders
            .map((order) => ({
                value: order.carrier_id,
                label: carrierDisplayName(order.order_carrier, client?.partnerCarriersById),
            }))
            .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
    }, [carriersData, client?.partnerCarriersById]);

    const TABS = useTabs({ page: 'oms' });

    const handleColor = useOrderColor();

    const handleDefaultExportClick = async () => {
        exportSelectedOrders();
    };

    const handleAccessorialExportClick = async () => {
        const potentialAccessorials = genAccessorials('CUSTOM', []);

        const uniqueOrders = new Set();
        const filteredOrderData = csvRowData.orderData.filter(({ order }) => {
            if (uniqueOrders.has(order.order_id)) {
                return false;
            }
            uniqueOrders.add(order.order_id);
            return true;
        });

        const accessorialData = filteredOrderData.reduce((acc, { order, revenue, owner, routeLabel, item }) => {
            const priceBreakdown = order.oms
                ? order?.price_breakdown?.internalBreakdown
                : Object.keys(clientsById).includes(order.shipper_id)
                ? order?.price_breakdown?.shipperBreakdown
                : order?.price_breakdown?.carrierBreakdown;

            const accessorials = priceBreakdown?.accessorials || [];
            const rows = accessorials.length
                ? accessorials.map((accessorial) => {
                      const matchingAccessorial = potentialAccessorials.find((a) => a.type === accessorial.type);
                      return {
                          order,
                          accessorial: {
                              ...accessorial,
                              label: accessorial.label || matchingAccessorial?.label || accessorial.type,
                          },
                          revenue,
                          owner,
                          routeLabel,
                          item,
                      };
                  })
                : [
                      {
                          order,
                          accessorial: null,
                          revenue,
                          owner,
                          routeLabel,
                          item,
                      },
                  ];

            return [...acc, ...rows];
        }, []);

        csvDownload({
            headers: OMS_ACCESSORIAL_EXPORT_COLUMNS.map((col) => col.header),
            data: accessorialData.map((row) => OMS_ACCESSORIAL_EXPORT_COLUMNS.map((col) => col.value(row))),
            filename: `onward-AccessorialExport-${new Date().toISOString()}.csv`,
            delimiter: ',',
        });
    };

    useEffect(() => {
        const tabValue = searchParams.get('tab') || null;
        if (tabValue) {
            let tabIndex = TABS.findIndex((tab) => tab.value === tabValue);
            setTabIndex(Math.max(tabIndex, 0));
        } else {
            setTabIndex(
                Math.max(
                    TABS.findIndex((tab) => tab.value === 'ALL'),
                    0
                )
            );
        }
    }, [TABS, searchParams]);

    const [getters] = useMemo(() => {
        const tuples = FILTER_ATTRS.map((attr) => [
            searchParams.get(attr),
            (val) => {
                setSearchParams((prev) => {
                    if (val) {
                        prev.set(attr, val);
                    } else {
                        prev.delete(attr);
                    }

                    return prev;
                });
            },
        ]);

        return [tuples.map(([getters]) => getters), tuples.map(([, setters]) => setters)];
    }, [searchParams]);

    // Initialize from URL params only on mount or when there's no local override
    useEffect(() => {
        const urlSearchTerm = searchParams.get('searchTerm') || '';
        if (searchOverride === '') {
            setSearch(urlSearchTerm);
        }
    }, [searchParams]);

    const handleOpenColumnFilter = useCallback(
        (columnId) => {
            if (filterableColumnIds.includes(columnId)) {
                setOpenColumnFilter(columnId);
            }
        },
        [filterableColumnIds]
    );

    const handleCloseColumnFilter = useCallback(() => setOpenColumnFilter(null), []);
    useEffect(() => {
        setOpenColumnFilter(null);
    }, [tabIndex]);

    const handleClearColumnFilter = useCallback(
        (columnId) => {
            const config = MYORDERS_COLUMN_FILTER_MAP[columnId];
            if (!config?.controls?.length) return;

            const resetValues = {};
            config.controls.forEach((control) => {
                if (control.key) {
                    resetValues[control.key] = null;
                }
                if (control.keys) {
                    control.keys.forEach((k) => {
                        resetValues[k] = null;
                    });
                }
            });

            if (Object.keys(resetValues).length) {
                applyFilters(resetValues);
            }

            if (openColumnFilter === columnId) {
                setOpenColumnFilter(null);
            }
        },
        [applyFilters, openColumnFilter]
    );

    const getWhereFilters = useCallback(
        (tabIdx, extraFilters = null) => {
            const [
                startDate,
                endDate,
                schedDelStart,
                schedDelEnd,
                estShipStart,
                estShipEnd,
                actualReceivedStart,
                actualReceivedEnd,
                warehouseStartDate,
                warehouseEndDate,
                warehouseStatus,
                manufacturer,
                orderType,
                volume,
                distance,
                scheduledStatus,
                shipper,
                carrier,
                dispatchIntegrationType,
                middleMileStatus,
                serviceType,
                locationType,
                milestone,
                assignedAdminId,
            ] = getters;

            const searchTerm = searchParams.get('searchTerm');

            const whereFilters = [
                {
                    _or: [
                        { shipper_id: { _in: clientIds } },
                        { carrier_id: { _in: clientIds } },
                        { middle_mile_carrier_id: { _in: clientIds } },
                        { warehouse_carrier_id: { _in: clientIds } },
                        { contractor_id: { _in: clientIds } },
                    ],
                },
                ...(TABS?.[tabIdx]?.filters || []),
            ];

            if (TABS?.[tabIdx]?.value === 'INCOMPLETE') {
                whereFilters.push({
                    _or: [
                        { job: { client_id: { _in: clientIds } } },
                        ...(circles?.[ASSET_HEAVY]
                            ? [{ carrier_id: { _in: clientIds } }, { warehouse_carrier_id: { _in: clientIds } }]
                            : []),
                    ],
                });
            }

            if (milestone) {
                const milestoneFilter = MILESTONE_OPTIONS.find((o) => o.value === milestone)?.filter;
                if (milestoneFilter) {
                    whereFilters.push(milestoneFilter);
                }
            }

            if (assignedAdminId) {
                whereFilters.push({ assigned_admin_id: { _eq: assignedAdminId } });
            }

            if (serviceType) {
                whereFilters.push({ service_level_id: { _eq: serviceType } });
            }

            if (locationType) {
                whereFilters.push({
                    _or: [
                        {
                            _and: [
                                { order_type: { _neq: 'return' } },
                                { dropoff_location_type: { _eq: locationType } },
                            ],
                        },
                        {
                            _and: [{ order_type: { _eq: 'return' } }, { pickup_location_type: { _eq: locationType } }],
                        },
                    ],
                });
            }

            if (startDate) {
                whereFilters.push({ created_at: { _gte: startDate } });
            }

            if (endDate) {
                whereFilters.push({ created_at: { _lt: addOneDay(endDate) } });
            }
            if (schedDelStart) {
                whereFilters.push({ delivery_date: { _gte: schedDelStart } });
            }

            if (schedDelEnd) {
                whereFilters.push({ delivery_date: { _lt: addOneDay(schedDelEnd) } });
            }

            if (estShipStart) {
                whereFilters.push({
                    wh_events: {
                        _and: [{ action: { _eq: 'START:AWAITING' } }, { est_ship_date: { _gte: estShipStart } }],
                    },
                });
            }

            if (estShipEnd) {
                whereFilters.push({
                    wh_events: {
                        _and: [
                            { action: { _eq: 'START:AWAITING' } },
                            { est_ship_date: { _lte: addOneDay(estShipEnd) } },
                        ],
                    },
                });
            }

            if (actualReceivedStart) {
                whereFilters.push({
                    wh_events: {
                        _and: [
                            { transition: { next: { _eq: 'RECEIVING' } } },
                            { received_date: { _gte: actualReceivedStart } },
                        ],
                    },
                });
            }

            if (actualReceivedEnd) {
                whereFilters.push({
                    wh_events: {
                        _and: [
                            { transition: { next: { _eq: 'RECEIVING' } } },
                            { received_date: { _lt: addOneDay(actualReceivedEnd) } },
                        ],
                    },
                });
            }

            if (warehouseStartDate) {
                whereFilters.push({
                    wh_events: {
                        _and: [{ est_received_date: { _gte: warehouseStartDate } }],
                    },
                });
            }

            if (warehouseEndDate) {
                whereFilters.push({
                    wh_events: {
                        _and: [{ est_received_date: { _lte: addOneDay(warehouseEndDate) } }],
                    },
                });
            }

            if (warehouseStatus === 'RECEIVED') {
                whereFilters.push({
                    _or: [
                        { warehouse_status: { _eq: 'RECEIVED' } },
                        { wh_events: { status: { _eq: 'RECEIVED' } } },
                        { wh_events: { received_date: { _is_null: false } } },
                        { wh_events: { transition: { next: { _eq: 'RECEIVING' } } } },
                    ],
                });
            } else if (warehouseStatus === 'NOT_DELIVERED') {
                whereFilters.push({
                    _and: [
                        { _not: { warehouse_status: { _eq: 'RECEIVED' } } },
                        { _not: { wh_events: { status: { _eq: 'RECEIVED' } } } },
                        { _not: { wh_events: { received_date: { _is_null: false } } } },
                        { _not: { wh_events: { transition: { next: { _eq: 'RECEIVING' } } } } },
                    ],
                });
            }

            if (manufacturer) {
                whereFilters.push({ manufacturer: { _eq: manufacturer } });
            }

            // Warehouse inventory receipts have no consignee. They stay out of OMS until a
            // delivery is created from that stock with a customer name and address. Explicit
            // Order Type = Inventory still returns them for ops.
            if (orderType) {
                whereFilters.push({ order_type: { _eq: orderType } });
            } else {
                whereFilters.push({
                    _or: [{ order_type: { _is_null: true } }, { order_type: { _neq: 'wh_inventory' } }],
                });
            }

            if (searchTerm) {
                whereFilters.push({
                    _or: searchable.map((searchField) => {
                        const [col, nested] = searchField.split('.');
                        if (!!nested) {
                            return { [col]: { _contains: { [nested]: searchTerm } } };
                        }
                        return { [searchField]: { _ilike: `%${searchTerm}%` } };
                    }),
                });
            }

            if (shipper && shipper !== 'All') {
                whereFilters.push({ shipper_id: { _eq: shipper } });
            }

            if (carrier && carrier !== 'All') {
                whereFilters.push({ carrier_id: { _eq: carrier } });
            }

            if (volume) {
                const [minVolume, maxVolume] = volume.split('|');
                whereFilters.push({
                    _or: [
                        {
                            _and: [
                                { order_type: { _eq: 'return' } },
                                { returns_total_cubes: { _lte: parseInt(maxVolume) } },
                                { returns_total_cubes: { _gte: parseInt(minVolume) } },
                            ],
                        },
                        {
                            _and: [
                                { order_type: { _eq: 'delivery' } },
                                { items_total_cubes: { _lte: parseInt(maxVolume) } },
                                { items_total_cubes: { _gte: parseInt(minVolume) } },
                            ],
                        },
                        {
                            _and: [
                                { order_type: { _eq: 'exchange' } },
                                {
                                    _or: [
                                        {
                                            _and: [
                                                { returns_total_cubes: { _lte: parseInt(maxVolume) } },
                                                { returns_total_cubes: { _gte: parseInt(minVolume) } },
                                            ],
                                        },
                                        {
                                            _and: [
                                                { items_total_cubes: { _lte: parseInt(maxVolume) } },
                                                { items_total_cubes: { _gte: parseInt(minVolume) } },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                });
            }

            if (distance) {
                const [minDistance, maxDistance] = distance.split('|');
                whereFilters.push(
                    { miles: { _lte: parseInt(maxDistance) } },
                    { miles: { _gte: parseInt(minDistance) } }
                );
            }

            if (scheduledStatus) {
                whereFilters.push({ delivery_date: { _is_null: scheduledStatus === 'Unscheduled' } });
            }

            if (dispatchIntegrationType && dispatchIntegrationType !== 'ALL') {
                whereFilters.push({
                    dispatch_integration_type:
                        dispatchIntegrationType === 'NONE' ? { _is_null: true } : { _eq: dispatchIntegrationType },
                });
            }

            if (middleMileStatus) {
                whereFilters.push({
                    middle_mile_integration_status:
                        middleMileStatus === 'NONE' ? { _is_null: true } : { _eq: middleMileStatus },
                });
            }

            if (extraFilters?.length) {
                whereFilters.push({ _and: extraFilters });
            }

            return { _and: whereFilters };
        },
        [getters, searchParams, TABS, clientIds]
    );

    const where = useMemo(() => {
        return getWhereFilters(tabIndex);
    }, [getWhereFilters, tabIndex]);

    const order_by = useMemo(() => {
        const ascOrDesc = sortBy?.desc ? 'desc_nulls_last' : 'asc_nulls_last';
        switch (sortBy?.id) {
            case 'deldate':
                return [{ delivery_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'delivery_date_scheduled_at':
                return [{ delivery_date_scheduled_at: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'last_modified_at':
                return [{ last_modified_at: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'shipper':
                return [{ order_shipper: { business_name: ascOrDesc } }, { order_number: 'desc_nulls_last' }];
            case 'est_ship_date':
                return [{ est_ship_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'est_received_date':
                return [{ est_received_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'received_date':
                return [{ received_date: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'po':
            case 'po_number':
                return [{ po_number: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'ordernum':
            case 'order_number':
                return [{ order_number: ascOrDesc }, { created_at: 'desc_nulls_last' }];
            case 'created_at':
                return [{ created_at: ascOrDesc }, { order_number: 'desc_nulls_last' }];
            case 'dropoff':
                return [
                    { dropoff_state: ascOrDesc },
                    { dropoff_city: ascOrDesc },
                    { dropoff_zip: ascOrDesc },
                    { order_number: 'desc_nulls_last' },
                ];
            default:
                return [{ created_at: 'desc_nulls_last' }, { order_number: 'desc_nulls_last' }];
        }
    }, [sortBy]);

    const PAGE_SIZE = 100;

    const {
        loading: loadingOrdersByUserId,
        data,
        fetchMore,
        networkStatus,
    } = useQuery(QUERY_ORDERS_WITH_DRIVERS_BY_CARRIER_ID_OR_SHIPPER_ID, {
        variables: {
            limit: PAGE_SIZE,
            offset: 0,
            where,
            order_by,
        },
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            setTotalCount(data.orders_aggregate.aggregate.totalCount);
        },
        onError: (error) => {
            captureException(error);
            console.log(error);
        },
    });

    const [upsertWhEvent] = useMutation(UPSERT_WH_EVENT, {
        onError: (error) => {
            captureException(error);
            console.error(error);
        },
    });

    const [upsertEvents, { loading: upsertEventsLoading }] = useMutation(UPSERT_EVENTS, {
        refetchQueries: [QUERY_ORDERS_WITH_DRIVERS_BY_CARRIER_ID_OR_SHIPPER_ID],
        awaitRefetchQueries: true,
        onError: (error) => {
            captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Error receiving orders' });
        },
    });

    const [updateOrdersMany, { loading: completeOrdersLoading }] = useMutation(UPDATE_ORDERS_MANY, {
        refetchQueries: [QUERY_ORDERS_WITH_DRIVERS_BY_CARRIER_ID_OR_SHIPPER_ID],
        awaitRefetchQueries: true,
        onError: (error) => {
            captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Error updating orders' });
        },
    });
    const [upsertOrder, { loading: upsertOrderLoading }] = useMutation(UPSERT_ORDERS, {
        refetchQueries: [QUERY_ORDERS_WITH_DRIVERS_BY_CARRIER_ID_OR_SHIPPER_ID],
        awaitRefetchQueries: true,
        onError: (error) => {
            captureException(error);
            console.error(error);
            setNotification({ severity: 'error', message: 'Failed to update order. Please try again.' });
        },
    });

    const onSaveOrder = useCallback(
        async ({
            errors,
            order: modified,
            items: itemsModified,
            itemsRemoved,
            itemUpdates,
            tags,
            tagsRemoved,
            documents,
            orderIds,
            subscriptions,
            services,
            servicesRemoved,
        }) => {
            if (errors?.hasMissingField?.length) {
                setNotification({
                    severity: 'error',
                    message: `Order has missing fields: ${errors.hasMissingField.join(', ')}.`,
                });
                return;
            }
            if (errors?.phoneInvalid) {
                setNotification({ severity: 'error', message: 'Order has an invalid phone number.' });
                return;
            }

            if (errors?.failedPricing?.length) {
                let message = `Failed to calculate pricing for ${modified.order_number}.`;
                if (errors.failedPricing.includes('miles')) {
                    message = `${message} Cannot find route between pickup and dropoff locations.`;
                }
                if (errors.failedPricing.includes('delivery_type')) {
                    message = `${message} ${
                        modified.order_type === 'return' ? 'Return Pickup' : 'Delivery'
                    } type not set.`;
                }
                setNotification({ severity: 'warning', message });
            }

            const { notes, ...rest } = modified;

            // This slows down save by a few seconds but ensures the order data
            // is updated with the saved values if edit modal is reopened before
            // the refetch completes
            await upsertOrder({
                variables: {
                    orders: [
                        {
                            ...withWarehouseEstimateEvent(rest),
                            notes: {
                                data: notes,
                                on_conflict: {
                                    constraint: 'notes_pkey',
                                    update_columns: ['note'],
                                },
                            },
                        },
                    ],
                    items: itemsModified || [],
                    removals: itemsRemoved || [],
                    item_updates: itemUpdates || [],
                    tags: tags,
                    tag_removals: tagsRemoved,
                    order_ids: orderIds,
                    documents: documents || [],
                    email_subscriptions: subscriptions || [],
                    services: services || [],
                    service_removals: servicesRemoved || [],
                },
            });

            if (modified.delivery_date !== editingOrder?.delivery_date) {
                const toInsert = patchEvent({
                    prev: editingOrder?.wh_events || [],
                    next: 'PICKED_UP',
                    patch: { est_delivery_date: modified.delivery_date },
                }).map(({ __typename, state, transition, driver, wh_driver, location, ...remaining }) => remaining);

                if (toInsert[0]?.order_id) {
                    upsertWhEvent({
                        variables: {
                            events: toInsert,
                            update_cols: Object.keys(toInsert[0]),
                        },
                    });
                }
            }

            setEditingOrder(null);
        },
        [upsertOrder, upsertWhEvent, editingOrder]
    );
    const [allLoadedOrders, setAllLoadedOrders] = useState([]);
    const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

    const filterKeyRef = useRef(null);
    useEffect(() => {
        const key = JSON.stringify([where, order_by]);
        if (filterKeyRef.current === null) {
            filterKeyRef.current = key;
            return;
        }
        if (filterKeyRef.current !== key) {
            filterKeyRef.current = key;
            setFetchMoreLoading(false);
            clearSelection();
        }
    }, [where, order_by, clearSelection]);

    useEffect(() => {
        if (!data?.orders) return;
        setAllLoadedOrders(data.orders);
    }, [data?.orders]);

    const allOrders = allLoadedOrders;

    const isRowSelectedFn = useCallback(
        (id) => (selectAllActive ? !excludedOrderIds[id] : !!selectedOrderIds[id]),
        [selectAllActive, excludedOrderIds, selectedOrderIds]
    );

    const getEffectiveSelectedOrders = useCallback(
        () => (allOrders || []).filter((order) => isRowSelectedFn(order.order_id)),
        [allOrders, isRowSelectedFn]
    );

    const selectedCount = useMemo(() => {
        if (selectAllActive) {
            return Math.max(0, allOrders.length - Object.keys(excludedOrderIds).length);
        }
        return Object.values(selectedOrderIds).filter(Boolean).length;
    }, [selectAllActive, allOrders.length, excludedOrderIds, selectedOrderIds]);

    const hasSelection = selectedCount > 0;

    const selectionStateRef = useRef({ selectAllActive: false, excluded: {}, selected: {} });
    const selectionListenersRef = useRef(new Set());

    useEffect(() => {
        selectionStateRef.current = { selectAllActive, excluded: excludedOrderIds, selected: selectedOrderIds };
        selectionListenersRef.current.forEach((listener) => listener());
    }, [selectAllActive, excludedOrderIds, selectedOrderIds]);

    const selectionController = useMemo(() => {
        const toggleInMap = (setMap, id) =>
            setMap((prev) => {
                const next = { ...prev };
                if (next[id]) delete next[id];
                else next[id] = true;
                return next;
            });

        return {
            subscribe: (listener) => {
                selectionListenersRef.current.add(listener);
                return () => selectionListenersRef.current.delete(listener);
            },
            isRowSelected: (id) => {
                const s = selectionStateRef.current;
                return s.selectAllActive ? !s.excluded[id] : !!s.selected[id];
            },
            getHeaderState: () => {
                const s = selectionStateRef.current;
                if (s.selectAllActive) return Object.keys(s.excluded).length ? 'some' : 'all';
                return Object.values(s.selected).some(Boolean) ? 'some' : 'none';
            },
            toggleRow: (id) => {
                const s = selectionStateRef.current;
                return s.selectAllActive ? toggleInMap(setExcludedOrderIds, id) : toggleInMap(setSelectedOrderIds, id);
            },
            toggleAll: () => {
                const s = selectionStateRef.current;
                if (s.selectAllActive && !Object.keys(s.excluded).length) {
                    clearSelection();
                } else {
                    setSelectedOrderIds({});
                    setExcludedOrderIds({});
                    setSelectAllActive(true);
                }
            },
        };
    }, [clearSelection]);

    const exportOrderRevenue = (order) => {
        return calcOrderPricing(order, clientIds);
    };

    const exportOrderOwner = (order) => {
        return calcOrderOwner(order, clientIds);
    };

    const csvRowData = useMemo(() => {
        const orderData = (allOrders || [])
            .filter((order) => isRowSelectedFn(order.order_id))
            .reduce((acc, order) => {
                let revenue = exportOrderRevenue(order);
                let owner = exportOrderOwner(order);
                let routeLabel =
                    order.routes && order.routes.length > 0
                        ? order.routes
                              .map((routeMapping) => routeMapping.route.route_alias || routeMapping.route.route_number)
                              .join(', ')
                        : '';

                const rows = order.itemsByOrderId.map((item) => {
                    return { order, item, revenue, owner, routeLabel };
                });
                return [...acc, ...rows];
            }, []);
        return { orderData: orderData };
    }, [isRowSelectedFn, allOrders]);

    const manufacturersList = useMemo(() => {
        const manufacturers = allOrders.reduce((acc, order) => {
            if (order.manufacturer) {
                acc[order.manufacturer] = true;
            }
            return acc;
        }, {});

        return Object.entries(manufacturers)
            .filter((_, enabled) => enabled)
            .map(([manufacturer]) => ({
                value: manufacturer,
                label: manufacturer,
            }));
    }, [allOrders]);

    const handleSearch = (e) => {
        const searchText = e.target.value;
        setSearch(searchText);
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        setDebounceTimer(
            setTimeout(() => {
                setSearchParams((prev) => {
                    if (searchText) {
                        prev.set('searchTerm', searchText.trim());
                    } else {
                        prev.delete('searchTerm');
                    }

                    return prev;
                });
            }, 500)
        );
    };

    const handleTabChange = (e, tab) => {
        const tabValue = TABS[tab].value;
        setTabIndex(tab);
        setSearchParams((prev) => {
            prev.set('tab', tabValue);
            return prev;
        });
    };

    const handleSortBy = useCallback(
        (newSortBy) => {
            if (newSortBy?.id !== sortBy.id || newSortBy.desc !== sortBy.desc) {
                setSortBy(newSortBy);
            }
        },
        [sortBy]
    );

    const memoizedSortBy = useMemo(() => [sortBy], [sortBy]);
    const tableCallbacks = useMemo(
        () => ({
            handleSortBy,
            setSelectedOrderIds,
            handleColor,
        }),
        [handleSortBy, setSelectedOrderIds, handleColor]
    );
    const activeTab = useMemo(() => TABS[tabIndex], [TABS, tabIndex]);

    // The table renders `allLoadedOrders`, a React state copy that loadMoreOrders appends
    // fetchMore results to. Those appended rows are snapshots taken at fetch time and are never
    // re-read, so a row edited after it was paged in keeps its pre-edit values even once the
    // mutation has refreshed the cache
    const openOrderForEdit = useCallback(
        (order) => {
            const fresh = apolloClient.cache.readFragment({
                id: apolloClient.cache.identify({ __typename: 'orders', order_id: order.order_id }),
                fragment: ORDER_FIELDS,
                fragmentName: 'OrderFields',
            });

            setEditingOrder(fresh ? { ...order, ...fresh } : order);
        },
        [apolloClient]
    );

    const TABLE_COLUMNS = useColumns({
        orders: allOrders,
        tab: activeTab,
        onwardAdminsById,
        callbacks: {
            onEditOrder: openOrderForEdit,
            isOrderEditable,
        },
    });

    const tableExportOverridesById = useMemo(
        () => ({
            warehousestatus: ({ order }) => {
                const warehouse = aggregateEvents(order.wh_events);
                if (warehouse.status === 'RECEIVED') return 'Received';
                const [received, total] = (order.itemsByOrderId || []).reduce(
                    ([recAcc, totAcc], item) => [
                        recAcc + (RECEIVED_STATUSES.includes(item.pallet?.warehouse_status) ? item.quantity || 1 : 0),
                        totAcc + (item.quantity || 1),
                    ],
                    [0, 0]
                );
                return received > 0 ? `${received}/${total} Received` : 'Not Received';
            },
            vol: ({ order }) => EXPORT_INT_FMT.format(orderTotalCubesForExport(order) || 0),
            weight: ({ order }) => EXPORT_INT_FMT.format(orderTotalWeightForExport(order) || 0),
            estimated_delivery_range: ({ order }) => {
                if (!order?.estimated_delivery_range_end) return '-';
                return `${order.estimated_delivery_range_start_formatted} - ${order.estimated_delivery_range_end_formatted}`;
            },
            loaded_at: ({ order }) =>
                formatExportDate(
                    order?.loading_completed ??
                        findMatchingWarehouseEvent(order?.wh_events, { action: 'PICKED_UP:CONFIRM' })?.pickup_date,
                    EXPORT_DATE_TIME_FMT
                ),
            ofd_at: ({ order }) => formatExportDate(order?.ltl_pickup_completed, EXPORT_DATE_TIME_FMT),
            arrived_at: ({ order }) => formatExportDate(order?.ltl_dropoff_arrival, EXPORT_DATE_TIME_FMT),
            completed_at: ({ order }) => formatExportDate(order?.completion_time, EXPORT_DATE_TIME_FMT),
            pod_present: ({ order }) => (exportHasAnyValue(order?.pod) ? 'Yes' : '-'),
            photos_present: ({ order }) => (exportHasAnyValue(order?.delivery_photo) ? 'Yes' : '-'),
            middle_mile_integration_status: ({ order }) => {
                const v = order.middle_mile_integration_status;
                return v ? startCase(v.toLowerCase()).replace('_', '-') : '--';
            },
        }),
        []
    );

    const tableColumnExportersById = useMemo(() => {
        const map = {};
        flattenTableLeafColumns(TABLE_COLUMNS).forEach((col) => {
            if (!col?.id || NON_EXPORTABLE_COLUMN_IDS.has(col.id)) return;
            const header = typeof col.Header === 'string' && col.Header ? col.Header : col.id;
            const override = tableExportOverridesById[col.id];
            map[col.id] = {
                header,
                value: override
                    ? override
                    : ({ order }) => {
                          if (typeof col.accessor === 'function') {
                              const raw = col.accessor(order);
                              if (raw == null) return '-';
                              if (raw instanceof Date) return formatExportDate(raw);
                              return raw;
                          }
                          return '-';
                      },
            };
        });
        return map;
    }, [TABLE_COLUMNS, tableExportOverridesById]);

    const highlightRow = useRowHighlight(activeTab);

    const initialLoading = loadingOrdersByUserId && allLoadedOrders.length === 0;
    const hasMore = useMemo(() => allLoadedOrders.length < totalCount, [allLoadedOrders.length, totalCount]);

    const fetchMoreInFlightRef = useRef(false);

    const loadMoreOrders = useCallback(() => {
        if (!hasMore || fetchMoreInFlightRef.current) return;

        fetchMoreInFlightRef.current = true;
        setFetchMoreLoading(true);
        // Use allLoadedOrders.length as the offset - this is the source of truth
        const currentOffset = allLoadedOrders.length;
        const timeoutId = setTimeout(() => {
            if (fetchMoreInFlightRef.current) {
                fetchMoreInFlightRef.current = false;
                setFetchMoreLoading(false);
                setNotification({
                    severity: 'warning',
                    message: 'Loading more orders is taking longer than expected. Please try again or refresh.',
                });
            }
        }, 30000);

        fetchMore({
            variables: {
                limit: PAGE_SIZE,
                offset: currentOffset,
                where,
                order_by,
            },
        })
            .then((result) => {
                const newOrders = result?.data?.orders || [];
                if (newOrders.length > 0) {
                    setAllLoadedOrders((prev) => [...prev, ...newOrders]);
                }
            })
            .catch((e) => {
                captureException(e);
                console.error(e);
                setNotification({
                    severity: 'error',
                    message: 'Failed to load more orders. Please refresh and try again.',
                });
            })
            .finally(() => {
                clearTimeout(timeoutId);
                fetchMoreInFlightRef.current = false;
                setFetchMoreLoading(false);
            });
    }, [hasMore, fetchMore, PAGE_SIZE, where, order_by, allLoadedOrders.length]);

    const ordersTableElement = useMemo(
        () => (
            <CustomizableTable
                key={tableKey}
                orders={allOrders}
                columns={TABLE_COLUMNS}
                tab={activeTab}
                onRowClick={(order) => {
                    switch (activeTab.value) {
                        case 'INCOMPLETE':
                            if (order.job_id) {
                                window.open(`/job/${order.job_id}`, '_blank');
                            }
                            break;
                        default: {
                            const url = `${window.location.origin}/order/${order.order_id}`;
                            window.open(url, '_blank');
                            break;
                        }
                    }
                }}
                enableRowSelection={true}
                selectionCheckboxScale={1.2}
                selectedRowIds={selectedOrderIds}
                onSelectionChange={setSelectedOrderIds}
                selectionController={selectionController}
                sortBy={memoizedSortBy}
                onSortChange={handleSortBy}
                highlightRow={highlightRow}
                callbacks={tableCallbacks}
                milestoneThresholdSettings={milestone_threshold_settings}
                view={accountType}
                useDiv={true}
                virtualizeRows={true}
                onLoadMore={loadMoreOrders}
                loadMoreThreshold={800}
                hasMore={hasMore}
                isLoading={initialLoading || fetchMoreLoading}
                filterableColumns={filterableColumnIds}
                onOpenColumnFilter={handleOpenColumnFilter}
                onClearColumnFilter={handleClearColumnFilter}
                initialState={tableInitialState}
                onLayoutChange={setTableLayoutState}
            />
        ),
        [
            allOrders,
            selectionController,
            memoizedSortBy,
            tableCallbacks,
            activeTab,
            TABLE_COLUMNS,
            highlightRow,
            accountType,
            milestone_threshold_settings,
            navigate,
            loadMoreOrders,
            hasMore,
            initialLoading,
            fetchMoreLoading,
            filterableColumnIds,
            handleOpenColumnFilter,
            handleClearColumnFilter,
            tableKey,
            tableInitialState,
        ]
    );

    const receiveSelectedOrders = () => {
        const nowIso = new Date().toISOString();
        let skippedPickupOrders = 0;
        const { eligible: receivableOrders, skippedSplit } = partitionSplitParents(getEffectiveSelectedOrders());
        const events = receivableOrders.reduce((acc, order) => {
            if (!order) return acc;

            if ((order.wh_events || []).some((e) => e.action === 'START:PICKING_UP')) {
                skippedPickupOrders += 1;
                return acc;
            }

            const baseEvents =
                order.event_state === 'WAREHOUSE_RETURN'
                    ? (() => {
                          const attemptEvent =
                              [...(order.wh_events || [])]
                                  .reverse()
                                  .find((e) => e.transition?.next === 'WAREHOUSE_RETURN') || null;
                          const attemptAction = attemptEvent?.action;
                          return patchEvent({
                              curr: [],
                              prev: order.wh_events || [],
                              action: attemptAction,
                              patch: { status: 'RECEIVED', received_date: nowIso },
                          });
                      })()
                    : receive(order, {}, { received_date: nowIso }) || [];

            const upsertable = baseEvents
                .filter((event) => !!event)
                .map((event) => {
                    const existing = findMatchingWarehouseEvent(order.wh_events, { action: event.action });
                    return existing?.event_id ? { ...existing, ...event } : event;
                });
            if (!upsertable.length) return acc;

            return [...acc, ...upsertable.map(toWarehouseEventInput)];
        }, []);

        const trackingEvents = buildBulkReceiveTrackingEvents({
            orders: receivableOrders,
            eventTime: nowIso,
        });

        const receivedOrderCount = new Set([...events.map((e) => e.order_id), ...trackingEvents.map((e) => e.order_id)])
            .size;

        const skipMessage = bulkSkipMessage({ pickupCount: skippedPickupOrders, splitCount: skippedSplit.length });
        if (skipMessage) {
            setNotification({ severity: 'warning', message: skipMessage });
        }

        if (!events.length && !trackingEvents.length) return;

        upsertEvents({
            variables: { events, tracking_events: trackingEvents },
            onCompleted: () => {
                setNotification({
                    severity: skipMessage ? 'warning' : 'success',
                    message: skipMessage
                        ? `Received ${receivedOrderCount} orders. ${skipMessage}`
                        : `Received ${receivedOrderCount} orders`,
                });
            },
        });
    };

    const completeSelectedOrders = () => {
        const nowIso = new Date().toISOString();
        const { eligible, skippedSplit } = partitionSplitParents(getEffectiveSelectedOrders());
        const completableOrders = eligible.filter((order) => order.order_status !== 'complete');
        const orderIds = completableOrders.map((order) => order.order_id);

        const skipMessage = bulkSkipMessage({ splitCount: skippedSplit.length });
        if (skipMessage) {
            setNotification({ severity: 'warning', message: skipMessage });
        }

        if (!orderIds.length) return;

        // Completing an order means its items were delivered, so the item timeline has to move too —
        // forward_stocking_items reads nothing else to decide whether stock has left the warehouse.
        const trackingEvents = buildBulkCompletionTrackingEvents({ orders: completableOrders, eventTime: nowIso });

        updateOrdersMany({
            variables: {
                updates: [
                    {
                        where: { order_id: { _in: orderIds } },
                        _set: {
                            order_status: 'complete',
                            completion_time: nowIso,
                            completion_source: COMPLETION_SOURCES.WEB,
                        },
                    },
                ],
                events: [],
                tracking_events: trackingEvents,
            },
            onCompleted: ({ update_orders_many }) => {
                const affected = update_orders_many?.[0]?.affected_rows || 0;
                setNotification({
                    severity: skipMessage ? 'warning' : 'success',
                    message: skipMessage
                        ? `Completed ${affected} orders. ${skipMessage}`
                        : `Completed ${affected} orders`,
                });
                setSelectedOrderIds({});
            },
        });
    };

    const [sendMessage, { loading: sendingMessage }] = useAction(SEND_MESSAGES, {
        onComplete: () => {
            setNotification({
                severity: 'success',
                message: 'Messages Sent!',
            });
        },
        onError: (err) => {
            console.error(err);
            setNotification({
                severity: 'error',
                message: 'Failed to send messages. Please try again.',
            });
        },
    });

    const exportSelectedOrders = () => {
        setOpenExportModal(true);
    };

    const itemAddOnColumns = useMemo(
        () => ORDER_EXPORT_COLUMNS.filter((col) => ITEM_EXPORT_HEADERS.has(col.header)),
        []
    );

    const csvExportColumns = useMemo(() => {
        const tableCols = buildExportColumnList({
            leafIds: flattenTableLeafColumns(TABLE_COLUMNS)
                .map((c) => c?.id)
                .filter(Boolean),
            savedOrder: tableLayoutState.columnOrder || [],
            hiddenColumns: tableLayoutState.hiddenColumns || [],
            exportersById: tableColumnExportersById,
            exportOnlyColumns: EXPORT_ONLY_COLUMNS,
        });

        return { Order: [...tableCols, ...itemAddOnColumns] };
    }, [
        TABLE_COLUMNS,
        tableColumnExportersById,
        tableLayoutState.columnOrder,
        tableLayoutState.hiddenColumns,
        itemAddOnColumns,
    ]);

    const [insertTemplate, { loading: insertTemplateLoading }] = useMutation(INSERT_CSV_EXPORT_TEMPLATE, {
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'csv_export_templates' });
        },
        onError: (error) => {
            captureException(error);
            console.log(error.message);
        },
    });

    const tabScrollComponent = useCallback(
        ({ style, onClick, ...props }) => {
            return (
                <>
                    <div
                        css={css`
                            display: flex;
                            flex-direction: row;
                            align-items: center;
                            ${props.direction === 'right' &&
                            `
                        padding-left: 12px;
                        border-left: 2px solid lightgray;
                    `}
                            ${props.direction === 'left' &&
                            `
                        padding-right: 12px;
                        border-right: 2px solid lightgray;
                    `}
                    ${props.disabled && 'opacity: 0;'}
                        `}
                        onClick={onClick}
                    >
                        <TabScrollButton
                            sx={{
                                color: 'red',
                                fontSize: 'large',
                            }}
                            style={{
                                fontSize: 'large',
                            }}
                            {...props}
                            css={css`
                                & svg {
                                    font-size: 2.5rem;
                                    color: black;
                                }
                            `}
                        />
                    </div>
                </>
            );
        },
        [tabIndex, TABS]
    );

    return (
        <>
            <EditOrderModal
                order={editingOrder}
                open={!!editingOrder}
                opt={{
                    readOnly: !!editingOrder?.carrier_invoice_id,
                    enableSingleOrderManifest: true,
                    enableAdminTab: isCorporateAdminCombinedView,
                    adminTabShipperIds: orgShipperIds,
                    adminTabCarrierIds: orgCarrierIds,
                }}
                callbacks={{
                    onSave: onSaveOrder,
                    onClose: () => setEditingOrder(null),
                    onError: (error) => {
                        setNotification({
                            severity: 'error',
                            message: error?.message || error || `We're sorry, something went wrong.`,
                        });
                    },
                }}
            />
            <ThemeProvider theme={theme}>
                <MyOrdersColumnFilterPanel
                    open={Boolean(openColumnFilter)}
                    columnId={openColumnFilter}
                    onClose={handleCloseColumnFilter}
                    applyFilters={applyFilters}
                    searchParams={searchParams}
                    shippers={shipperOptions}
                    carriers={carrierOptions}
                    manufacturersList={manufacturersList}
                    onwardAdminOptions={onwardAdminOptions}
                />
                <Box
                    className={`${sidebarCollapsed && 'collapse-margin'}  table-height`}
                    sx={{ flexGrow: 1, padding: 0, height: 'calc(100vh - 50px)' }}
                >
                    <Snackbar
                        open={!!notification?.message}
                        {...(notification || {})}
                        handleClose={() => setNotification({})}
                    />
                    <Grid container direction="column" wrap="nowrap" className="h-100">
                        <Grid container justifyContent="center" className="bg-white border-bottom">
                            <OnwardTabContainer
                                value={tabIndex}
                                onChange={handleTabChange}
                                textColor="primary"
                                indicatorColor="primary"
                                css={css`
                                    background-color: white;
                                    & .MuiTabs-flexContainer {
                                        justify-content: center;
                                    }
                                `}
                                ScrollButtonComponent={tabScrollComponent}
                            >
                                {TABS.map((tab, idx) => (
                                    <OnwardTab
                                        label={tab.label}
                                        value={idx}
                                        style={{ outline: 0 }}
                                        key={`tab-${idx}`}
                                        error={tab.red}
                                    />
                                ))}
                            </OnwardTabContainer>
                        </Grid>
                        <PrimaryHeaderContainer item>
                            <Grid container justifyContent="space-between">
                                <Grid
                                    item
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        gap: 12px;
                                    `}
                                >
                                    <PrimaryHeaderLabel>My Orders</PrimaryHeaderLabel>
                                    <Typography
                                        css={css`
                                            font-size: 14px;
                                            font-weight: 500;
                                            color: #4c4c4c;
                                        `}
                                    >
                                        {totalCount} orders
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        value={searchOverride}
                                        placeholder="Search orders"
                                        onChange={handleSearch}
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        className="me-3"
                                        InputProps={{
                                            style: { backgroundColor: 'white' },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        select
                                        variant="outlined"
                                        size="small"
                                        label={profiles?.length ? 'Profile' : 'Select Profile...'}
                                        InputLabelProps={{ shrink: true }}
                                        {...profileSelectProps}
                                        className="me-3"
                                        InputProps={{ style: { backgroundColor: 'white', minWidth: 220 } }}
                                    >
                                        <MenuItem value="">Default</MenuItem>
                                        {profiles.map((p) => (
                                            <MenuItem key={p.order_table_profile_id} value={p.order_table_profile_id}>
                                                {p.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <SendMessageModal
                                        open={openSendMessage}
                                        callbacks={{
                                            onClose: () => setOpenSendMessage(false),
                                            sendMessage: (message) => {
                                                const order_ids = getEffectiveSelectedOrders().map(
                                                    (order) => order.order_id
                                                );
                                                return sendMessage({ message, order_ids });
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </PrimaryHeaderContainer>
                        {(initialLoading || fetchMoreLoading) && (
                            <Grid item className="ms-5 me-5">
                                <LinearProgress
                                    color="primary"
                                    css={css`
                                        width: 100%;
                                    `}
                                />
                            </Grid>
                        )}
                        {selectAllActive && (
                            <Grid item className="ms-5 me-5">
                                <Box
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        gap: 8px;
                                        padding: 8px 12px;
                                        background-color: #eaf5ec;
                                        border: 1px solid #59b863;
                                        border-radius: 4px;
                                        font-size: 14px;
                                        font-weight: 500;
                                        color: #2b2b2b;
                                    `}
                                >
                                    {selectedCount >= totalCount && !Object.keys(excludedOrderIds).length ? (
                                        <span>All {totalCount} orders are selected.</span>
                                    ) : (
                                        <span>
                                            Auto-selecting orders as they load — {selectedCount} of {totalCount}{' '}
                                            selected. Scroll down to load and select the rest.
                                        </span>
                                    )}
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={clearSelection}
                                        css={css`
                                            font-weight: 700;
                                        `}
                                    >
                                        Clear selection
                                    </Link>
                                </Box>
                            </Grid>
                        )}
                        <Grid item className="ms-5 me-5 mb-3 bg-white" style={{ overflow: 'hidden', flexGrow: 1 }}>
                            {ordersTableElement}
                        </Grid>
                        <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            css={css`
                                width: auto;
                                box-shadow: 0px -4px 4px 0px #37373752;
                                min-height: 75px !important;
                            `}
                        >
                            {/* Export Button */}
                            <PrimaryButton
                                variant="contained"
                                color="primary"
                                disabled={!hasSelection}
                                onClick={handleDefaultExportClick}
                                css={css`
                                    margin-left: 1rem;
                                    height: 36px;
                                `}
                            >
                                Export
                            </PrimaryButton>

                            {/* Export Accessorials Button */}
                            <PrimaryButton
                                variant="contained"
                                color="primary"
                                disabled={!hasSelection}
                                onClick={handleAccessorialExportClick}
                                css={css`
                                    margin-left: 1rem;
                                    height: 36px;
                                `}
                            >
                                Export Accessorials
                            </PrimaryButton>

                            {/* Mark Received Button */}
                            <PrimaryButton
                                variant="contained"
                                color="primary"
                                onClick={receiveSelectedOrders}
                                css={css`
                                    margin-left: 1rem;
                                    height: 36px;
                                `}
                                disabled={upsertEventsLoading || !hasSelection}
                            >
                                Mark Received
                            </PrimaryButton>

                            {/* Mark Complete Button */}
                            <PrimaryButton
                                variant="contained"
                                color="primary"
                                onClick={completeSelectedOrders}
                                css={css`
                                    margin-left: 1rem;
                                    height: 36px;
                                `}
                                disabled={completeOrdersLoading || !hasSelection}
                            >
                                Mark Complete
                            </PrimaryButton>

                            {/* Message Customer Button */}
                            <PrimaryButton
                                variant="contained"
                                color="primary"
                                onClick={() => setOpenSendMessage(true)}
                                css={css`
                                    margin-left: 1rem;
                                    height: 36px;
                                `}
                                disabled={sendingMessage || !hasSelection}
                            >
                                Message Customer
                            </PrimaryButton>
                            <div
                                css={css`
                                    margin-left: 1rem;
                                `}
                            >
                                <CustomizableTableProfileControl
                                    disabled={profilesLoading}
                                    tableType="MY_ORDERS"
                                    currentProfileName={currentProfileName}
                                    currentProfileId={currentProfileId}
                                    selectedProfileId={selectedProfileId}
                                    onSaveNew={handleSaveNewProfile}
                                    onOverwrite={handleOverwriteProfile}
                                    onImportById={handleImportProfileById}
                                    onDeleteCurrent={handleDeleteCurrentProfile}
                                    saving={insertProfileLoading}
                                    overwriting={updateProfileLoading}
                                    deleting={deleteProfileLoading}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Box>

                <ExportCsvTemplateModal
                    open={openExportModal}
                    setOpen={setOpenExportModal}
                    exportType={'ORDER'}
                    csvExportColumns={csvExportColumns}
                    callbacks={{
                        onExport: async (selectedColumns, templateName, exportType, includeAccessorials) => {
                            const exportColumnsForRun = {
                                ...csvExportColumns,
                                Order: [...(csvExportColumns.Order || [])],
                            };

                            if (templateName) {
                                insertTemplate({
                                    variables: {
                                        object: {
                                            name: templateName,
                                            user_id: templateOwnerId,
                                            export_type: exportType,
                                            template: {
                                                ...selectedColumns,
                                                Order: {
                                                    ...selectedColumns.Order,
                                                    ...(includeAccessorials && { Accessorials: true }),
                                                },
                                            },
                                        },
                                    },
                                });
                            }

                            if (includeAccessorials) {
                                const potentialAccessorials = genAccessorials('CUSTOM', []);

                                // Each accessorial column asks again for the same order, so resolve once
                                // per order rather than once per cell.
                                const breakdowns = new WeakMap();
                                const getBreakdown = (order) => {
                                    if (!breakdowns.has(order)) {
                                        breakdowns.set(order, calcOrderBreakdown(order, clientIds));
                                    }

                                    return breakdowns.get(order);
                                };

                                let maxAccessorialCount = csvRowData.orderData.reduce((acc, row) => {
                                    const priceBreakdown = getBreakdown(row.order);

                                    const accessorialCount = priceBreakdown?.accessorials?.length || 0;
                                    return Math.max(acc, accessorialCount);
                                }, 1);

                                const accCols = [
                                    {
                                        header: 'Base Rate',
                                        value: ({ order }) => {
                                            const priceBreakdown = getBreakdown(order);
                                            return priceBreakdown?.base_charge || '-';
                                        },
                                    },
                                    ...Array(maxAccessorialCount)
                                        .fill(0)
                                        .flatMap((_, idx) => [
                                            {
                                                header: `Accessorial ${idx + 1} Type`,
                                                value: ({ order }) => {
                                                    const priceBreakdown = getBreakdown(order);
                                                    const accessorialType = priceBreakdown?.accessorials[idx]?.type;
                                                    if (!accessorialType) return '-';
                                                    const accessorial = potentialAccessorials.find(
                                                        (a) => a.type === accessorialType
                                                    );
                                                    return accessorial?.label || accessorialType;
                                                },
                                            },
                                            {
                                                header: `Accessorial ${idx + 1} Subtotal`,
                                                value: ({ order }) => {
                                                    const priceBreakdown = getBreakdown(order);
                                                    return priceBreakdown?.accessorials[idx]?.quantity
                                                        ? `$${Number(
                                                              priceBreakdown?.accessorials[idx]?.quantity *
                                                                  priceBreakdown?.accessorials[idx]?.rate
                                                          ).toFixed(2)}`
                                                        : '-';
                                                },
                                            },
                                        ]),
                                ];

                                exportColumnsForRun['Order'] = [...exportColumnsForRun['Order'], ...accCols];
                            }

                            // Filter out duplicate orders if they don't want item info
                            let filteredOrderData = csvRowData.orderData;
                            if (!selectedColumns.Order.Item) {
                                const uniqueOrders = new Set();
                                filteredOrderData = csvRowData.orderData.filter(({ order }) => {
                                    if (uniqueOrders.has(order.order_id)) {
                                        return false;
                                    }
                                    uniqueOrders.add(order.order_id);
                                    return true;
                                });
                            }

                            Object.keys(exportColumnsForRun).forEach((colType) => {
                                const finalExportCols = exportColumnsForRun[colType].filter(
                                    (col) =>
                                        col.header === 'Base Rate' ||
                                        col.header.toLowerCase().includes('accessorial') ||
                                        selectedColumns[colType][col.header]
                                );

                                if (finalExportCols.length > 0) {
                                    csvDownload({
                                        headers: finalExportCols.map((col) => col.header),
                                        data: filteredOrderData.map((row) =>
                                            finalExportCols.map((col) => col.value(row))
                                        ),
                                        filename: `onward-${colType}Export-${new Date().toISOString()}.csv`,
                                        delimiter: ',',
                                    });
                                }
                            });
                        },
                    }}
                />
            </ThemeProvider>
        </>
    );
};

export default MyOrders;
